import {
  addHours,
  addSeconds,
  differenceInSeconds,
  isWithinInterval,
} from 'date-fns';
import { useEffect, useState } from 'react';
import { formatTime } from '../../../../../core/commom/formatTime';
import { Button } from '../../../../../core/components/atoms/Button';
import { Typography } from '../../../../../core/components/molecules/Typography';

interface iLiveDetailFooterProps {
  eventId: string;
  endDate: Date;
  startDate: Date;
  hasRecordAvailable: boolean;
  onOpenClass: (eventId: string) => Promise<void>;
  isLoading?: boolean;
}

function AprovaLiveDetailFooter({
  eventId,
  startDate,
  endDate,
  hasRecordAvailable,
  onOpenClass,
  isLoading = false,
}: iLiveDetailFooterProps) {
  const [now, setNow] = useState(new Date());

  const secondsToRetry = 60;
  const [lastTryDate, setLastTryDate] = useState<Date | null>();
  const [timeToRetryIsAllowed, setTimeToRetryIsAllowed] = useState(0);
  const [retryJoinMessage, setRetryJoinMessage] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!lastTryDate) {
      return;
    }

    const nextTry = addSeconds(lastTryDate, secondsToRetry);
    const timeDiffInSeconds = differenceInSeconds(nextTry, now);

    setTimeToRetryIsAllowed(timeDiffInSeconds);

    if (timeDiffInSeconds > 0) {
      setRetryJoinMessage(
        `Parece que a sala ainda não foi gerada, aguarde ${formatTime(timeDiffInSeconds)} e tente novamente.`
      );
      return;
    }

    setRetryJoinMessage('');
  }, [now]);

  async function handleOpenClass() {
    onOpenClass(eventId).catch(() => {
      setLastTryDate(new Date());
    });
  }

  useEffect(() => {
    setLastTryDate(null);
    setTimeToRetryIsAllowed(0);
    setRetryJoinMessage('');
  }, [eventId]);

  const itsHappeningNow = isWithinInterval(new Date(), {
    start: startDate,
    end: endDate,
  });
  const oneHourToStart = now > addHours(startDate, -1);

  if (now < startDate && !oneHourToStart) {
    return (
      <div className='flex flex-col items-center justify-center gap-xl'>
        <Typography size='small'>
          O link ficará disponível 1h antes do início da aula.
        </Typography>
      </div>
    );
  }

  if (now > endDate) {
    return (
      <div className='flex flex-col items-center justify-center gap-xl'>
        <Typography size='small'>Essa aula já aconteceu.</Typography>
        {hasRecordAvailable && (
          <Button variant='outlined' full onClick={handleOpenClass}>
            Ver gravação
          </Button>
        )}
      </div>
    );
  }

  const message =
    oneHourToStart && !itsHappeningNow
      ? 'Essa aula já vai começar!'
      : 'Essa aula está acontecendo!';

  return (
    <div className='flex flex-col items-center justify-center gap-xl'>
      {retryJoinMessage ? (
        <Typography
          size='small'
          className='text-center text-feedback-color-warning-pure'
        >
          {retryJoinMessage}
        </Typography>
      ) : (
        <Typography size='small'>{message}</Typography>
      )}
      <Button
        full
        onClick={handleOpenClass}
        loading={isLoading}
        disabled={timeToRetryIsAllowed > 0}
      >
        Acessar aula
      </Button>
    </div>
  );
}
export { AprovaLiveDetailFooter };
